import { Gift, Gift2 } from '@shared/icons';
import type { IconProps } from '@shared/icons/types';
import type { ComponentType, ReactNode } from 'react';
import { AuthClientBoundary } from '../../../AuthClientBoundary/AuthClientBoundary';
import { LoginOrSignUp } from '../../../LoginOrSignUp/LoginOrSignUp';
import { Bonus } from '../../../svg/Bonus/Bonus';
import { FreeSpins } from '../../../svg/FreeSpins/FreeSpins';
import type { PromotionsPageType } from '../../types/types';

type PromotionsLoginOrSignUpProps = {
  children: ReactNode;
  isProtectedRoute: boolean;
  route: PromotionsPageType;
};

const icons = new Map<PromotionsPageType, ComponentType<IconProps>>([
  ['bonuses', Bonus],
  ['offers', Gift],
  ['free-spins', FreeSpins],
  ['free-bets', Gift2],
]);

/**
 * Guards a promotions section behind authentication.
 *
 * When the section is protected, logged-out visitors see a route-specific login/sign-up
 * prompt while logged-in visitors see the section content; unprotected sections always
 * render their children. Authentication gating is delegated to {@link AuthClientBoundary},
 * so this component itself needs no session hook and no client boundary.
 *
 * @component
 * @param children - The section content to render when access is allowed.
 * @param protectedRoute - Whether this section requires the user to be logged in.
 * @param route - The current section, used to pick the prompt icon and translation namespace.
 * @returns The login/sign-up prompt or the provided children.
 */
function PromotionsLoginOrSignUp({ children, isProtectedRoute, route }: PromotionsLoginOrSignUpProps) {
  if (!isProtectedRoute) {
    return <>{children}</>;
  }

  const namespace = `offers.login_or_signup.${route}`;
  const IconComponent = icons.get(route);

  return (
    <>
      <AuthClientBoundary.LoggedIn>{children}</AuthClientBoundary.LoggedIn>
      <AuthClientBoundary.LoggedOut>
        <LoginOrSignUp icon={IconComponent} namespace={namespace} className="ll-container-inner pt-10 pb-20" />
      </AuthClientBoundary.LoggedOut>
    </>
  );
}

export { PromotionsLoginOrSignUp };
